<script setup>
import { ref, onMounted, defineProps, watch, computed } from 'vue'
import axios from 'axios'

const props = defineProps({
  tableName: String
})

const columns = ref([])
const loading = ref(false)
const showAddColumn = ref(false)
const showInlineAdd = ref(false)
const showDeleteConfirm = ref(false)
const columnToDelete = ref(null)

// Column selection and export functionality
const selectedColumns = ref(new Set())
const showExportDropdown = ref(false)

// Column search and navigation
const columnSearchQuery = ref('')
const showColumnSearch = ref(false)
const matchedColumns = ref([])
const currentMatchIndex = ref(-1)
const newColumn = ref({
  name: '',
  type: 'varchar',
  length: 255,
  nullable: true,
  default: '',
  primary: false,
  autoIncrement: false
})

const columnTypes = [
  'varchar', 'text', 'int', 'bigint', 'decimal', 'float', 'double',
  'boolean', 'date', 'datetime', 'timestamp', 'time', 'json'
]

const loadTableStructure = async () => {
  if (!props.tableName) return
  
  loading.value = true
  try {
    const response = await axios.get(`/api/database-tables/${props.tableName}/structure`)
    columns.value = response.data.columns
  } catch (error) {
    console.error('Error loading table structure:', error)
  } finally {
    loading.value = false
  }
}

const addColumn = async () => {
  try {
    await axios.post(`/api/database-tables/${props.tableName}/columns`, newColumn.value)
    await loadTableStructure()
    showAddColumn.value = false
    resetNewColumn()
  } catch (error) {
    console.error('Error adding column:', error)
  }
}

const confirmDeleteColumn = (columnName) => {
  columnToDelete.value = columnName
  showDeleteConfirm.value = true
}

const dropColumn = async () => {
  if (!columnToDelete.value) return
  
  try {
    await axios.delete(`/api/database-tables/${props.tableName}/columns/${columnToDelete.value}`)
    await loadTableStructure()
    showDeleteConfirm.value = false
    columnToDelete.value = null
  } catch (error) {
    console.error('Error dropping column:', error)
    alert('Error dropping column: ' + (error.response?.data?.message || error.message))
  }
}

const cancelDelete = () => {
  showDeleteConfirm.value = false
  columnToDelete.value = null
}

const resetNewColumn = () => {
  newColumn.value = {
    name: '',
    type: 'varchar',
    length: 255,
    nullable: true,
    default: '',
    primary: false,
    autoIncrement: false
  }
}

const addColumnInline = async () => {
  if (!newColumn.value.name.trim()) {
    alert('Column name is required')
    return
  }
  
  try {
    await axios.post(`/api/database-tables/${props.tableName}/columns`, newColumn.value)
    await loadTableStructure()
    showInlineAdd.value = false
    resetNewColumn()
  } catch (error) {
    console.error('Error adding column:', error)
    alert('Error adding column: ' + (error.response?.data?.message || error.message))
  }
}

const cancelInlineAdd = () => {
  showInlineAdd.value = false
  resetNewColumn()
}

// Column selection functions
const toggleColumnSelection = (columnName) => {
  if (selectedColumns.value.has(columnName)) {
    selectedColumns.value.delete(columnName)
  } else {
    selectedColumns.value.add(columnName)
  }
  selectedColumns.value = new Set(selectedColumns.value) // Trigger reactivity
}

const selectAllColumns = () => {
  selectedColumns.value = new Set(columns.value.map(col => col.name))
}

const deselectAllColumns = () => {
  selectedColumns.value = new Set()
}

const isAllSelected = computed(() => {
  return columns.value.length > 0 && selectedColumns.value.size === columns.value.length
})

const isPartiallySelected = computed(() => {
  return selectedColumns.value.size > 0 && selectedColumns.value.size < columns.value.length
})

// Export functions
const exportToSqlCreate = () => {
  const selectedCols = columns.value.filter(col => selectedColumns.value.has(col.name))
  if (selectedCols.length === 0) {
    alert('Please select at least one column to export')
    return
  }

  const columnDefinitions = selectedCols.map(col => {
    let definition = `  \`${col.name}\` ${col.type.toUpperCase()}`
    
    if (col.type.toLowerCase().includes('varchar') && col.length) {
      definition += `(${col.length})`
    }
    
    if (!col.nullable) {
      definition += ' NOT NULL'
    }
    
    if (col.default) {
      definition += ` DEFAULT '${col.default}'`
    }
    
    if (col.extra && col.extra.toLowerCase().includes('auto_increment')) {
      definition += ' AUTO_INCREMENT'
    }
    
    return definition
  }).join(',\n')

  const primaryKeys = selectedCols.filter(col => col.key === 'PRI').map(col => col.name)
  const primaryKeyClause = primaryKeys.length > 0 ? `,\n  PRIMARY KEY (\`${primaryKeys.join('`, `')}\`)` : ''

  const sql = `CREATE TABLE \`${props.tableName}\` (\n${columnDefinitions}${primaryKeyClause}\n);`
  
  copyToClipboard(sql)
  showExportDropdown.value = false
}

const exportToSqlInsert = () => {
  const selectedCols = columns.value.filter(col => selectedColumns.value.has(col.name))
  if (selectedCols.length === 0) {
    alert('Please select at least one column to export')
    return
  }

  const columnNames = selectedCols.map(col => `\`${col.name}\``).join(', ')
  const values = selectedCols.map(col => {
    if (col.type.toLowerCase().includes('int') || col.type.toLowerCase().includes('decimal') || col.type.toLowerCase().includes('float')) {
      return '0'
    } else if (col.type.toLowerCase().includes('date') || col.type.toLowerCase().includes('time')) {
      return "'1970-01-01'"
    } else if (col.type.toLowerCase().includes('bool')) {
      return 'FALSE'
    } else {
      return "''"
    }
  }).join(', ')

  const sql = `INSERT INTO \`${props.tableName}\` (${columnNames}) VALUES (${values});`
  
  copyToClipboard(sql)
  showExportDropdown.value = false
}

const exportToSqlUpdate = () => {
  const selectedCols = columns.value.filter(col => selectedColumns.value.has(col.name))
  if (selectedCols.length === 0) {
    alert('Please select at least one column to export')
    return
  }

  const setParts = selectedCols.map(col => {
    let value = "''"
    if (col.type.toLowerCase().includes('int') || col.type.toLowerCase().includes('decimal') || col.type.toLowerCase().includes('float')) {
      value = '0'
    } else if (col.type.toLowerCase().includes('date') || col.type.toLowerCase().includes('time')) {
      value = "'1970-01-01'"
    } else if (col.type.toLowerCase().includes('bool')) {
      value = 'FALSE'
    }
    return `\`${col.name}\` = ${value}`
  }).join(', ')

  const primaryKey = columns.value.find(col => col.key === 'PRI')
  const whereClause = primaryKey ? `WHERE \`${primaryKey.name}\` = 1` : 'WHERE 1=1'

  const sql = `UPDATE \`${props.tableName}\` SET ${setParts} ${whereClause};`
  
  copyToClipboard(sql)
  showExportDropdown.value = false
}

const exportToSqlSelect = () => {
  const selectedCols = columns.value.filter(col => selectedColumns.value.has(col.name))
  if (selectedCols.length === 0) {
    alert('Please select at least one column to export')
    return
  }

  const columnNames = selectedCols.map(col => `\`${col.name}\``).join(', ')
  const sql = `SELECT ${columnNames} FROM \`${props.tableName}\`;`
  
  copyToClipboard(sql)
  showExportDropdown.value = false
}

const exportToSqlAlter = () => {
  const selectedCols = columns.value.filter(col => selectedColumns.value.has(col.name))
  if (selectedCols.length === 0) {
    alert('Please select at least one column to export')
    return
  }

  const alterStatements = selectedCols.map(col => {
    let definition = `\`${col.name}\` ${col.type.toUpperCase()}`
    
    if (col.type.toLowerCase().includes('varchar') && col.length) {
      definition += `(${col.length})`
    }
    
    if (!col.nullable) {
      definition += ' NOT NULL'
    }
    
    if (col.default) {
      definition += ` DEFAULT '${col.default}'`
    }
    
    if (col.extra && col.extra.toLowerCase().includes('auto_increment')) {
      definition += ' AUTO_INCREMENT'
    }
    
    return `ALTER TABLE \`target_table_name\` ADD COLUMN ${definition};`
  })

  // Add a comment block with additional ALTER options
  const sqlHeader = `-- ALTER TABLE statements for selected columns from '${props.tableName}'
-- Replace 'target_table_name' with your actual target table name
-- 
-- Available ALTER operations:
-- ADD COLUMN: Add new columns to an existing table
-- MODIFY COLUMN: Change column definition (MySQL)
-- ALTER COLUMN: Change column definition (PostgreSQL/SQL Server)
-- DROP COLUMN: Remove columns from table
--
-- Example usage:
-- ALTER TABLE my_table ADD COLUMN column_name VARCHAR(255) NOT NULL;
-- ALTER TABLE my_table MODIFY COLUMN column_name TEXT;
-- ALTER TABLE my_table DROP COLUMN column_name;

`

  const sql = sqlHeader + alterStatements.join('\n')
  
  copyToClipboard(sql)
  showExportDropdown.value = false
}

const exportToCsv = () => {
  const selectedCols = columns.value.filter(col => selectedColumns.value.has(col.name))
  if (selectedCols.length === 0) {
    alert('Please select at least one column to export')
    return
  }

  const headers = selectedCols.map(col => col.name).join(',')
  const rows = selectedCols.map(col => {
    return [
      col.name,
      col.type,
      col.nullable ? 'YES' : 'NO',
      col.key || '',
      col.default || '',
      col.extra || ''
    ].map(val => `"${val}"`).join(',')
  }).join('\n')

  const csv = `Column,Type,Nullable,Key,Default,Extra\n${rows}`
  
  downloadFile(csv, `${props.tableName}_structure.csv`, 'text/csv')
  showExportDropdown.value = false
}

const exportToJson = () => {
  const selectedCols = columns.value.filter(col => selectedColumns.value.has(col.name))
  if (selectedCols.length === 0) {
    alert('Please select at least one column to export')
    return
  }

  const jsonData = {
    table: props.tableName,
    columns: selectedCols.map(col => ({
      name: col.name,
      type: col.type,
      nullable: col.nullable,
      key: col.key || null,
      default: col.default || null,
      extra: col.extra || null
    })),
    exported_at: new Date().toISOString(),
    selected_count: selectedCols.length,
    total_count: columns.value.length
  }

  const json = JSON.stringify(jsonData, null, 2)
  
  downloadFile(json, `${props.tableName}_structure.json`, 'application/json')
  showExportDropdown.value = false
}

// Utility functions
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    alert('SQL copied to clipboard!')
  } catch (err) {
    console.error('Failed to copy to clipboard:', err)
    // Fallback for older browsers
    const textArea = document.createElement('textarea')
    textArea.value = text
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
    alert('SQL copied to clipboard!')
  }
}

const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
  alert(`File downloaded: ${filename}`)
}

// Column search functionality
const searchColumns = () => {
  if (!columnSearchQuery.value.trim()) {
    matchedColumns.value = []
    currentMatchIndex.value = -1
    return
  }

  const query = columnSearchQuery.value.toLowerCase()
  matchedColumns.value = columns.value
    .map((col, index) => ({ column: col, index }))
    .filter(item => item.column.name.toLowerCase().includes(query))
  
  currentMatchIndex.value = matchedColumns.value.length > 0 ? 0 : -1
  
  if (matchedColumns.value.length > 0) {
    scrollToColumn(matchedColumns.value[0].column.name)
  }
}

const nextMatch = () => {
  if (matchedColumns.value.length === 0) return
  
  currentMatchIndex.value = (currentMatchIndex.value + 1) % matchedColumns.value.length
  scrollToColumn(matchedColumns.value[currentMatchIndex.value].column.name)
}

const previousMatch = () => {
  if (matchedColumns.value.length === 0) return
  
  currentMatchIndex.value = currentMatchIndex.value === 0 
    ? matchedColumns.value.length - 1 
    : currentMatchIndex.value - 1
  scrollToColumn(matchedColumns.value[currentMatchIndex.value].column.name)
}

const scrollToColumn = (columnName) => {
  // Find the column cell element and scroll to it
  const columnCell = document.querySelector(`[data-column-name="${columnName}"]`)
  if (columnCell) {
    columnCell.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'nearest', 
      inline: 'center' 
    })
    
    // Highlight the column temporarily
    columnCell.classList.add('column-highlight')
    setTimeout(() => {
      columnCell.classList.remove('column-highlight')
    }, 2000)
  }
}

const clearColumnSearch = () => {
  columnSearchQuery.value = ''
  matchedColumns.value = []
  currentMatchIndex.value = -1
  showColumnSearch.value = false
}

const toggleColumnSearch = () => {
  showColumnSearch.value = !showColumnSearch.value
  if (showColumnSearch.value) {
    // Focus search input after showing
    setTimeout(() => {
      document.querySelector('#column-search-input')?.focus()
    }, 100)
  } else {
    clearColumnSearch()
  }
}

// Computed for showing records count
const recordsCount = computed(() => {
  const total = columns.value.length
  const selected = selectedColumns.value.size
  return {
    total,
    selected,
    showing: total // In structure view, we show all columns
  }
})

watch(() => props.tableName, () => {
  loadTableStructure()
  selectedColumns.value = new Set() // Clear selection when table changes
  clearColumnSearch() // Clear search when table changes
})

// Watch column search query
watch(columnSearchQuery, () => {
  searchColumns()
})

onMounted(() => {
  loadTableStructure()
})
</script>

<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col h-full relative" @click="showExportDropdown = false">
    <!-- Compact Header Section -->
    <div class="bg-gradient-to-r from-slate-50 to-gray-50 px-4 py-2 border-b border-gray-200 flex-shrink-0">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-2">
          <div class="p-1 bg-blue-100 rounded">
            <i class="fas fa-table text-blue-600 text-sm"></i>
          </div>
          <div>
            <h3 class="text-sm font-semibold text-gray-900">{{ tableName }}</h3>
            <p class="text-xs text-gray-500">
              {{ columns.length }} columns
              <span v-if="selectedColumns.size > 0" class="ml-2 text-blue-600 font-medium">
                ({{ selectedColumns.size }} selected)
              </span>
            </p>
          </div>
        </div>
        <div class="flex items-center space-x-2">
          <!-- Column Search Toggle -->
          <button
            @click="toggleColumnSearch"
            class="inline-flex items-center px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded transition-all duration-200 shadow-sm hover:shadow-md"
            :class="{ 'bg-indigo-800': showColumnSearch }"
            title="Search columns"
          >
            <i class="fas fa-search mr-1 text-xs"></i>
            Find Column
          </button>

          <!-- Export Dropdown -->
          <div class="relative">
            <button
              @click.stop="showExportDropdown = !showExportDropdown"
              class="inline-flex items-center px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded transition-all duration-200 shadow-sm hover:shadow-md"
              :disabled="selectedColumns.size === 0"
              :class="{ 'opacity-50 cursor-not-allowed': selectedColumns.size === 0 }"
            >
              <i class="fas fa-download mr-1 text-xs"></i>
              Export ({{ selectedColumns.size }})
              <i class="fas fa-chevron-down ml-1 text-xs"></i>
            </button>
            
            <!-- Export Dropdown Menu -->
            <div
              v-if="showExportDropdown"
              class="absolute left-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-30 export-dropdown"
              @click.stop
            >
              <div class="py-1">
                <!-- SQL Query Group -->
                <div class="px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
                  SQL Queries
                </div>
                <button
                  @click="exportToSqlSelect"
                  class="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <i class="fas fa-search mr-2 text-cyan-600"></i>
                  SQL SELECT
                </button>
                <button
                  @click="exportToSqlCreate"
                  class="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <i class="fas fa-database mr-2 text-blue-600"></i>
                  SQL CREATE
                </button>
                <button
                  @click="exportToSqlInsert"
                  class="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <i class="fas fa-plus-circle mr-2 text-green-600"></i>
                  SQL INSERT
                </button>
                <button
                  @click="exportToSqlUpdate"
                  class="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <i class="fas fa-edit mr-2 text-orange-600"></i>
                  SQL UPDATE
                </button>
                <button
                  @click="exportToSqlAlter"
                  class="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <i class="fas fa-wrench mr-2 text-yellow-600"></i>
                  SQL ALTER
                </button>
                
                <!-- File Export Group -->
                <div class="border-t border-gray-200 my-1"></div>
                <div class="px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
                  File Export
                </div>
                <button
                  @click="exportToCsv"
                  class="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <i class="fas fa-file-csv mr-2 text-purple-600"></i>
                  Export as CSV
                </button>
                <button
                  @click="exportToJson"
                  class="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <i class="fas fa-file-code mr-2 text-indigo-600"></i>
                  Export as JSON
                </button>
              </div>
            </div>
          </div>

          <button
            @click="showInlineAdd = true"
            class="inline-flex items-center px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded transition-all duration-200 shadow-sm hover:shadow-md"
            v-if="!showInlineAdd"
          >
            <i class="fas fa-plus mr-1 text-xs"></i>
            Add Column
          </button>
          <button
            @click="showAddColumn = !showAddColumn"
            class="inline-flex items-center px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded transition-all duration-200 shadow-sm hover:shadow-md"
            :class="{ 'bg-red-600 hover:bg-red-700': showAddColumn }"
          >
            <i class="fas mr-1 text-xs" :class="showAddColumn ? 'fa-times' : 'fa-sidebar'"></i>
            {{ showAddColumn ? 'Close' : 'Panel' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Column Search Bar -->
    <div v-if="showColumnSearch" class="bg-blue-50 border-b border-blue-200 px-4 py-3 animate-slideDown">
      <div class="flex items-center space-x-3">
        <div class="flex-1 relative">
          <input
            id="column-search-input"
            v-model="columnSearchQuery"
            type="text"
            placeholder="Search columns by name..."
            class="w-full pl-8 pr-10 py-2 text-sm border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            @keydown.escape="clearColumnSearch"
          />
          <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400 text-sm"></i>
          <button
            v-if="columnSearchQuery"
            @click="clearColumnSearch"
            class="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-400 hover:text-blue-600"
          >
            <i class="fas fa-times text-sm"></i>
          </button>
        </div>
        
        <!-- Search Results Info -->
        <div v-if="matchedColumns.length > 0" class="flex items-center space-x-2">
          <span class="text-sm text-blue-700 font-medium">
            {{ currentMatchIndex + 1 }} of {{ matchedColumns.length }}
          </span>
          <div class="flex space-x-1">
            <button
              @click="previousMatch"
              class="p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs"
              title="Previous match"
            >
              <i class="fas fa-chevron-up"></i>
            </button>
            <button
              @click="nextMatch"
              class="p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs"
              title="Next match"
            >
              <i class="fas fa-chevron-down"></i>
            </button>
          </div>
        </div>
        
        <!-- No Results -->
        <div v-else-if="columnSearchQuery && matchedColumns.length === 0" class="text-sm text-red-600">
          No columns found
        </div>
        
        <!-- Close Button -->
        <button
          @click="toggleColumnSearch"
          class="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
          title="Close search"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <!-- Main Content Area with Flex Layout -->
    <div class="flex-1 flex min-h-0 relative">
      <!-- Table Section -->
      <div class="flex-1 flex flex-col min-h-0 transition-all duration-300" :class="{ 'mr-80': showAddColumn }">
        <!-- Loading State -->
        <div v-if="loading" class="flex-1 flex items-center justify-center">
          <div class="flex flex-col items-center space-y-2">
            <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <p class="text-gray-500 text-xs">Loading...</p>
          </div>
        </div>

        <!-- Scrollable Table -->
        <div v-else class="flex-1 flex flex-col min-h-0">
          <div class="flex-1 overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <table class="min-w-full text-xs">
              <thead class="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th class="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 w-12">
                    <div class="flex items-center justify-center">
                      <input
                        type="checkbox"
                        :checked="isAllSelected"
                        :indeterminate="isPartiallySelected"
                        @change="isAllSelected ? deselectAllColumns() : selectAllColumns()"
                        class="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        title="Select all columns"
                      />
                    </div>
                  </th>
                  <th class="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">
                    <div class="flex items-center space-x-1">
                      <i class="fas fa-columns text-gray-400 text-xs"></i>
                      <span>Column</span>
                    </div>
                  </th>
                  <th class="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">
                    <div class="flex items-center space-x-1">
                      <i class="fas fa-code text-gray-400 text-xs"></i>
                      <span>Type</span>
                    </div>
                  </th>
                  <th class="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">
                    <div class="flex items-center space-x-1">
                      <i class="fas fa-question-circle text-gray-400 text-xs"></i>
                      <span>Null</span>
                    </div>
                  </th>
                  <th class="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">
                    <div class="flex items-center space-x-1">
                      <i class="fas fa-key text-gray-400 text-xs"></i>
                      <span>Key</span>
                    </div>
                  </th>
                  <th class="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">
                    <div class="flex items-center space-x-1">
                      <i class="fas fa-edit text-gray-400 text-xs"></i>
                      <span>Default</span>
                    </div>
                  </th>
                  <th class="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">
                    <div class="flex items-center space-x-1">
                      <i class="fas fa-cog text-gray-400 text-xs"></i>
                      <span>Extra</span>
                    </div>
                  </th>
                  <th class="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 w-16">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-100">
                <tr v-for="(column, index) in columns" :key="column.name" 
                    class="hover:bg-gray-50 transition-colors duration-200 h-8"
                    :class="{ 'bg-blue-50/30': index % 2 === 1, 'bg-blue-100/50': selectedColumns.has(column.name) }">
                  <td class="px-3 py-1 whitespace-nowrap">
                    <div class="flex items-center justify-center">
                      <input
                        type="checkbox"
                        :checked="selectedColumns.has(column.name)"
                        @change="toggleColumnSelection(column.name)"
                        class="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        :title="`Select ${column.name}`"
                      />
                    </div>
                  </td>
                  <td class="px-3 py-1 whitespace-nowrap" :data-column-name="column.name">
                    <div class="flex items-center space-x-2">
                      <div class="p-0.5 bg-blue-100 rounded">
                        <i class="fas fa-columns text-blue-600 text-xs"></i>
                      </div>
                      <span class="text-xs font-medium text-gray-900">{{ column.name }}</span>
                    </div>
                  </td>
                  <td class="px-3 py-1 whitespace-nowrap">
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      {{ column.type }}
                    </span>
                  </td>
                  <td class="px-3 py-1 whitespace-nowrap">
                    <span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
                          :class="column.nullable ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                      <i class="fas mr-1 text-xs" :class="column.nullable ? 'fa-check' : 'fa-times'"></i>
                      {{ column.nullable ? 'YES' : 'NO' }}
                    </span>
                  </td>
                  <td class="px-3 py-1 whitespace-nowrap">
                    <span v-if="column.key" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                      <i class="fas fa-key mr-1 text-xs"></i>
                      {{ column.key }}
                    </span>
                    <span v-else class="text-gray-400 text-xs">—</span>
                  </td>
                  <td class="px-3 py-1 whitespace-nowrap">
                    <span v-if="column.default" class="text-xs text-gray-900 font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                      {{ column.default }}
                    </span>
                    <span v-else class="text-gray-400 text-xs">—</span>
                  </td>
                  <td class="px-3 py-1 whitespace-nowrap">
                    <span v-if="column.extra" class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                      {{ column.extra }}
                    </span>
                    <span v-else class="text-gray-400 text-xs">—</span>
                  </td>
                  <td class="px-3 py-1 whitespace-nowrap text-center">
                    <button
                      @click="confirmDeleteColumn(column.name)"
                      class="inline-flex items-center justify-center w-6 h-6 text-red-600 hover:text-white hover:bg-red-600 rounded transition-all duration-200"
                      :disabled="column.key === 'PRI'"
                      :class="{ 'opacity-50 cursor-not-allowed': column.key === 'PRI' }"
                      title="Drop column"
                    >
                      <i class="fas fa-trash text-xs"></i>
                    </button>
                  </td>
                </tr>
                
                <!-- Compact Inline Add Column Row -->
                <tr v-if="showInlineAdd" class="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 animate-fadeIn">
                  <td class="px-3 py-1">
                    <div class="flex items-center justify-center">
                      <i class="fas fa-plus text-blue-500 text-xs"></i>
                    </div>
                  </td>
                  <td class="px-3 py-1">
                    <div class="relative">
                      <input
                        v-model="newColumn.name"
                        type="text"
                        placeholder="Column name"
                        class="w-full pl-6 pr-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                        @keydown.enter="addColumnInline"
                        @keydown.escape="cancelInlineAdd"
                      >
                      <i class="fas fa-columns absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs"></i>
                    </div>
                  </td>
                  <td class="px-3 py-1">
                    <select
                      v-model="newColumn.type"
                      class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                    >
                      <option v-for="type in columnTypes" :key="type" :value="type">{{ type }}</option>
                    </select>
                  </td>
                  <td class="px-3 py-1">
                    <select
                      v-model="newColumn.nullable"
                      class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                    >
                      <option :value="true">YES</option>
                      <option :value="false">NO</option>
                    </select>
                  </td>
                  <td class="px-3 py-1">
                    <select
                      v-model="newColumn.primary"
                      class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                    >
                      <option :value="false">None</option>
                      <option :value="true">PRI</option>
                    </select>
                  </td>
                  <td class="px-3 py-1">
                    <input
                      v-model="newColumn.default"
                      type="text"
                      placeholder="Default"
                      class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white font-mono"
                      @keydown.enter="addColumnInline"
                      @keydown.escape="cancelInlineAdd"
                    >
                  </td>
                  <td class="px-3 py-1">
                    <select
                      v-model="newColumn.autoIncrement"
                      class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                    >
                      <option :value="false">None</option>
                      <option :value="true">AUTO_INC</option>
                    </select>
                  </td>
                  <td class="px-3 py-1">
                    <div class="flex items-center justify-center space-x-1">
                      <button
                        @click="addColumnInline"
                        class="inline-flex items-center justify-center w-6 h-6 text-white bg-green-600 hover:bg-green-700 rounded transition-all duration-200"
                        title="Save column"
                      >
                        <i class="fas fa-check text-xs"></i>
                      </button>
                      <button
                        @click="cancelInlineAdd"
                        class="inline-flex items-center justify-center w-6 h-6 text-white bg-red-600 hover:bg-red-700 rounded transition-all duration-200"
                        title="Cancel"
                      >
                        <i class="fas fa-times text-xs"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Records Count Footer -->
          <div v-if="columns.length > 0" class="bg-gray-50 border-t border-gray-200 px-4 py-2 flex items-center justify-between text-xs text-gray-600">
            <div class="flex items-center space-x-4">
              <span class="font-medium">
                Showing {{ recordsCount.showing }} of {{ recordsCount.total }} columns
              </span>
              <span v-if="recordsCount.selected > 0" class="text-blue-600 font-medium">
                ({{ recordsCount.selected }} selected)
              </span>
            </div>
            
            <!-- Quick Stats -->
            <div class="flex items-center space-x-4">
              <div class="flex items-center space-x-2">
                <div class="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>{{ columns.filter(col => col.nullable).length }} nullable</span>
              </div>
              <div class="flex items-center space-x-2">
                <div class="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <span>{{ columns.filter(col => col.key === 'PRI').length }} primary keys</span>
              </div>
              <div class="flex items-center space-x-2">
                <div class="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span>{{ columns.filter(col => col.extra && col.extra.includes('auto_increment')).length }} auto increment</span>
              </div>
            </div>
          </div>

          <!-- Compact Empty State -->
          <div v-if="columns.length === 0" class="flex-1 flex items-center justify-center">
            <div class="flex flex-col items-center space-y-3">
              <div class="p-3 bg-gray-100 rounded-full">
                <i class="fas fa-table text-gray-400 text-lg"></i>
              </div>
              <div>
                <h3 class="text-sm font-medium text-gray-900">No columns found</h3>
                <p class="text-gray-500 text-xs mt-1">This table doesn't have any columns yet.</p>
              </div>
              <button
                @click="showInlineAdd = true"
                class="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <i class="fas fa-plus mr-1"></i>
                Add First Column
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Side Panel for Adding Columns (Contained within component) -->
      <div 
        class="absolute top-0 right-0 h-full bg-white border-l border-gray-200 shadow-xl transform transition-transform duration-300 ease-in-out z-20 w-80"
        :class="showAddColumn ? 'translate-x-0' : 'translate-x-full'"
      >
        <!-- Panel Header -->
        <div class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold text-gray-900 flex items-center">
              <i class="fas fa-plus-circle text-blue-600 mr-2"></i>
              Add New Column
            </h3>
            <button
              @click="showAddColumn = false; resetNewColumn()"
              class="inline-flex items-center justify-center w-8 h-8 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        
        <!-- Panel Content -->
        <div class="flex-1 p-4 space-y-6 overflow-y-auto h-full">
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">Column Name</label>
            <div class="relative">
              <input
                v-model="newColumn.name"
                type="text"
                class="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                placeholder="Enter column name"
              >
              <i class="fas fa-columns absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-4">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Data Type</label>
              <select
                v-model="newColumn.type"
                class="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
              >
                <option v-for="type in columnTypes" :key="type" :value="type">{{ type }}</option>
              </select>
            </div>

            <div v-if="['varchar', 'char'].includes(newColumn.type)">
              <label class="block text-sm font-semibold text-gray-700 mb-2">Length</label>
              <input
                v-model="newColumn.length"
                type="number"
                class="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                placeholder="255"
              >
            </div>
          </div>

          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">Default Value</label>
            <input
              v-model="newColumn.default"
              type="text"
              class="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white font-mono"
              placeholder="Leave empty for no default"
            >
          </div>

          <div class="space-y-3">
            <h4 class="text-sm font-semibold text-gray-700">Column Properties</h4>
            
            <label class="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors duration-200">
              <input v-model="newColumn.nullable" type="checkbox" class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3">
              <div class="flex-1">
                <span class="text-sm font-medium text-gray-700">Nullable</span>
                <p class="text-xs text-gray-500">Allow NULL values in this column</p>
              </div>
            </label>
            
            <label class="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors duration-200">
              <input v-model="newColumn.primary" type="checkbox" class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3">
              <div class="flex-1">
                <span class="text-sm font-medium text-gray-700">Primary Key</span>
                <p class="text-xs text-gray-500">Make this column a primary key</p>
              </div>
            </label>
            
            <label class="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors duration-200">
              <input v-model="newColumn.autoIncrement" type="checkbox" class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3">
              <div class="flex-1">
                <span class="text-sm font-medium text-gray-700">Auto Increment</span>
                <p class="text-xs text-gray-500">Automatically increment this column</p>
              </div>
            </label>
          </div>

          <!-- Panel Footer - now inside the scrollable content -->
          <div class="pt-4 border-t border-gray-200">
            <div class="flex justify-end space-x-3">
              <button
                @click="showAddColumn = false; resetNewColumn()"
                class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                @click="addColumn"
                class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-sm hover:shadow-md text-sm font-medium"
              >
                <i class="fas fa-plus mr-2"></i>
                Add Column
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal (Contained within component) -->
    <div v-if="showDeleteConfirm" class="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 rounded-lg">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all duration-300 animate-slideUp">
        <!-- Modal Header -->
        <div class="px-6 py-4 border-b border-gray-200">
          <div class="flex items-center">
            <div class="p-2 bg-red-100 rounded-lg mr-3">
              <i class="fas fa-exclamation-triangle text-red-600 text-lg"></i>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-gray-900">Delete Column</h3>
              <p class="text-sm text-gray-500">This action cannot be undone</p>
            </div>
          </div>
        </div>
        
        <!-- Modal Content -->
        <div class="px-6 py-4">
          <p class="text-gray-700 mb-3">
            Are you sure you want to delete the column:
          </p>
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
            <div class="flex items-center space-x-2">
              <div class="p-1 bg-blue-100 rounded">
                <i class="fas fa-columns text-blue-600 text-xs"></i>
              </div>
              <span class="font-mono text-sm font-semibold text-gray-900">{{ columnToDelete }}</span>
            </div>
          </div>
          <div class="bg-red-50 border border-red-200 rounded-lg p-3">
            <div class="flex items-start space-x-2">
              <i class="fas fa-exclamation-circle text-red-500 text-sm mt-0.5"></i>
              <div>
                <p class="text-red-800 text-sm font-medium">Warning</p>
                <p class="text-red-700 text-xs">All data in this column will be permanently lost. This action cannot be undone.</p>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Modal Footer -->
        <div class="px-6 py-4 bg-gray-50 rounded-b-xl border-t border-gray-200">
          <div class="flex justify-end space-x-3">
            <button
              @click="cancelDelete"
              class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200 text-sm font-medium"
            >
              Cancel
            </button>
            <button
              @click="dropColumn"
              class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 shadow-sm hover:shadow-md text-sm font-medium"
            >
              <i class="fas fa-trash mr-2"></i>
              Delete Column
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-out;
}

.animate-slideUp {
  animation: slideUp 0.3s ease-out;
}

/* Custom scrollbar - more compact */
.scrollbar-thin::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.scrollbar-track-gray-100::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 3px;
}

.scrollbar-thumb-gray-300::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.scrollbar-thumb-gray-300::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Firefox scrollbar */
.overflow-auto {
  scrollbar-width: thin;
  scrollbar-color: #d1d5db #f3f4f6;
}

/* Checkbox indeterminate state */
input[type="checkbox"]:indeterminate {
  background-color: #2563eb;
  border-color: #2563eb;
  background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M4 8h8'/%3e%3c/svg%3e");
}

/* Selected row highlighting */
tr.selected-row {
  background-color: rgba(59, 130, 246, 0.1) !important;
  border-left: 3px solid #3b82f6;
}

/* Export dropdown animation */
.export-dropdown {
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from { 
    opacity: 0; 
    transform: translateY(-10px) scale(0.95); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1); 
  }
}

/* Column search highlight animation */
.column-highlight {
  background: linear-gradient(45deg, #3b82f6, #1e40af) !important;
  color: white !important;
  transform: scale(1.02);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
  transition: all 0.3s ease;
  border-radius: 4px;
  position: relative;
  z-index: 10;
}

.column-highlight * {
  color: white !important;
}

/* Search bar slide down animation */
.animate-slideDown {
  animation: slideDownSearch 0.3s ease-out;
}

@keyframes slideDownSearch {
  from {
    opacity: 0;
    transform: translateY(-20px);
    max-height: 0;
  }
  to {
    opacity: 1;
    transform: translateY(0);
    max-height: 200px;
  }
}
</style> 